import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  type AvailableField,
  type SetGroupInput,
  type SetMappingInput,
  useBackfillIdentityMutation,
  useGetFormFieldGroupsQuery,
  useGetFormMappingsQuery,
  useSetFormFieldGroupsMutation,
  useSetFormMappingsMutation,
} from './api/onboardingApi';

/** The four supported field purposes. */
const FIELD_PURPOSES = [
  { key: 'email', label: 'Email' },
  { key: 'first_name', label: 'First Name' },
  { key: 'last_name', label: 'Last Name' },
  { key: 'company', label: 'Company' },
] as const;

/** Valid field group tags. */
const FIELD_GROUP_OPTIONS = [
  { value: 'identity', label: 'Identity' },
  { value: 'onboarding', label: 'Onboarding' },
  { value: 'pulse', label: 'Pulse' },
  { value: 'demographic', label: 'Demographic' },
  { value: 'manager', label: 'Manager' },
  { value: 'untagged', label: 'Untagged' },
] as const;

export function TypeformFormMappingsPage() {
  const { formId } = useParams<{ formId: string }>();
  const navigate = useNavigate();

  const { data, isLoading, error } = useGetFormMappingsQuery(
    { formId: formId ?? '' },
    { skip: !formId }
  );
  const [setMappings, { isLoading: isSaving }] = useSetFormMappingsMutation();
  const [backfill, { isLoading: isBackfilling }] = useBackfillIdentityMutation();

  // Field groups
  const { data: groupsData } = useGetFormFieldGroupsQuery(
    { formId: formId ?? '' },
    { skip: !formId }
  );
  const [setGroups, { isLoading: isSavingGroups }] = useSetFormFieldGroupsMutation();

  // Local state for editable mappings: purpose -> selected field ref
  const [mappingState, setMappingState] = useState<Record<string, string>>({});
  // Local state for field groups: fieldRef -> { group, canonicalRef }
  const [groupState, setGroupState] = useState<
    Record<string, { group: string; canonicalRef: string }>
  >({});
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(
    null
  );

  // Initialize local state from fetched data (only on first load, not on refetch)
  const mappingsInitialized = useRef(false);
  useEffect(() => {
    if (data?.mappings && !mappingsInitialized.current) {
      mappingsInitialized.current = true;
      const initial: Record<string, string> = {};
      for (const m of data.mappings) {
        initial[m.field_purpose] = m.typeform_field_ref;
      }
      setMappingState(initial);
    }
  }, [data?.mappings]);

  // Initialize group state from fetched data (only on first load)
  const groupsInitialized = useRef(false);
  useEffect(() => {
    if (groupsData?.groups && !groupsInitialized.current) {
      groupsInitialized.current = true;
      const initial: Record<string, { group: string; canonicalRef: string }> = {};
      for (const g of groupsData.groups) {
        initial[g.typeform_field_ref] = {
          group: g.field_group,
          canonicalRef: g.canonical_ref ?? '',
        };
      }
      setGroupState(initial);
    }
  }, [groupsData?.groups]);

  const availableFields: AvailableField[] = data?.available_fields ?? [];
  const canonicalRefs: string[] = groupsData?.canonical_refs ?? [];

  const handlePurposeChange = useCallback((purpose: string, ref: string) => {
    setMappingState((prev) => {
      if (ref === '') {
        const next = { ...prev };
        delete next[purpose];
        return next;
      }
      return { ...prev, [purpose]: ref };
    });
  }, []);

  const handleSave = async () => {
    if (!formId) return;
    setFeedback(null);

    const mappings: SetMappingInput[] = Object.entries(mappingState)
      .filter(([, ref]) => ref)
      .map(([purpose, ref]) => {
        const field = availableFields.find((f) => f.ref === ref);
        return {
          field_purpose: purpose,
          typeform_field_ref: ref,
          typeform_field_id: field?.id ?? '',
          field_title: field?.title ?? '',
        };
      });

    try {
      await setMappings({ formId, mappings }).unwrap();
      setFeedback({ type: 'success', message: 'Mappings saved successfully' });
    } catch {
      setFeedback({ type: 'error', message: 'Failed to save mappings' });
    }
  };

  const handleGroupChange = useCallback((fieldRef: string, group: string) => {
    setGroupState((prev) => ({
      ...prev,
      [fieldRef]: { group, canonicalRef: prev[fieldRef]?.canonicalRef ?? '' },
    }));
  }, []);

  const handleCanonicalRefChange = useCallback((fieldRef: string, canonicalRef: string) => {
    setGroupState((prev) => ({
      ...prev,
      [fieldRef]: { group: prev[fieldRef]?.group ?? 'untagged', canonicalRef },
    }));
  }, []);

  const handleSaveGroups = async () => {
    if (!formId) return;
    setFeedback(null);

    const groups: SetGroupInput[] = Object.entries(groupState)
      .filter(([, val]) => val.group && val.group !== 'untagged')
      .map(([fieldRef, val]) => {
        const field = availableFields.find((f) => f.ref === fieldRef);
        return {
          typeform_field_ref: fieldRef,
          field_group: val.group,
          canonical_ref: val.canonicalRef || null,
          field_title: field?.title ?? '',
        };
      });

    try {
      await setGroups({ formId, groups }).unwrap();
      setFeedback({ type: 'success', message: 'Field groups saved successfully' });
    } catch {
      setFeedback({ type: 'error', message: 'Failed to save field groups' });
    }
  };

  const handleBackfill = async () => {
    if (!formId) return;
    setFeedback(null);
    try {
      const result = await backfill({ formId }).unwrap();
      setFeedback({
        type: 'success',
        message: `Backfill complete: ${result.updated} updated, ${result.failed} failed out of ${result.total} total`,
      });
    } catch {
      setFeedback({ type: 'error', message: 'Failed to backfill identity' });
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !formId) {
    return <Alert severity="error">Failed to load form mappings</Alert>;
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <IconButton onClick={() => navigate('/admin/typeform?tab=forms')}>
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="h5">Field Mappings</Typography>
          <Typography variant="body2" color="text.secondary">
            {data?.form_type ?? 'Unknown'} &mdash; <code>{formId}</code>
          </Typography>
        </Box>
      </Box>

      {feedback && (
        <Alert severity={feedback.type} onClose={() => setFeedback(null)} sx={{ mb: 2 }}>
          {feedback.message}
        </Alert>
      )}

      {/* Available Fields */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Available Fields
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Fields from the latest submission's Typeform definition.
        </Typography>
        {availableFields.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No fields available. Submit at least one response to this form first.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Ref</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Field ID</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {availableFields.map((field) => (
                <TableRow key={field.ref ?? field.id}>
                  <TableCell>
                    <code>{field.ref}</code>
                  </TableCell>
                  <TableCell>{field.title}</TableCell>
                  <TableCell>{field.type}</TableCell>
                  <TableCell>
                    <Typography variant="caption" fontFamily="monospace">
                      {field.id}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* Purpose Mappings */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Purpose Mappings
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Map each identity purpose to a Typeform field. Email is required.
        </Typography>

        <Box display="flex" flexDirection="column" gap={2} mt={2}>
          {FIELD_PURPOSES.map(({ key, label }) => (
            <FormControl key={key} fullWidth size="small">
              <InputLabel>
                {label}
                {key === 'email' ? ' (required)' : ''}
              </InputLabel>
              <Select
                value={mappingState[key] ?? ''}
                label={`${label}${key === 'email' ? ' (required)' : ''}`}
                onChange={(e) => handlePurposeChange(key, e.target.value)}
              >
                <MenuItem value="">
                  <em>Not mapped</em>
                </MenuItem>
                {availableFields.map((field) => (
                  <MenuItem key={field.ref} value={field.ref ?? ''}>
                    {field.title ?? field.ref} ({field.type})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ))}
        </Box>

        <Box display="flex" gap={2} mt={3}>
          <Button variant="contained" onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save Mappings'}
          </Button>
          <Button variant="outlined" onClick={handleBackfill} disabled={isBackfilling}>
            {isBackfilling ? 'Running...' : 'Backfill Identity'}
          </Button>
        </Box>
      </Paper>

      {/* Field Group Tags */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Field Group Tags
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Tag each field with a logical group and optional canonical reference for cross-form
          equivalence.
        </Typography>

        {availableFields.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No fields available to tag.
          </Typography>
        ) : (
          <Table size="small" sx={{ mt: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell>Field</TableCell>
                <TableCell>Type</TableCell>
                <TableCell sx={{ minWidth: 160 }}>Group</TableCell>
                <TableCell sx={{ minWidth: 250 }}>Canonical Ref</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {availableFields.map((field) => {
                const ref = field.ref ?? '';
                const current = groupState[ref];
                return (
                  <TableRow key={ref}>
                    <TableCell>
                      <Typography variant="body2">{field.title ?? ref}</Typography>
                      <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                        {ref}
                      </Typography>
                    </TableCell>
                    <TableCell>{field.type}</TableCell>
                    <TableCell>
                      <FormControl size="small" fullWidth>
                        <Select
                          value={current?.group ?? 'untagged'}
                          onChange={(e) => handleGroupChange(ref, e.target.value)}
                        >
                          {FIELD_GROUP_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <Autocomplete
                        freeSolo
                        size="small"
                        options={canonicalRefs}
                        value={current?.canonicalRef ?? ''}
                        onInputChange={(_, value) => handleCanonicalRefChange(ref, value)}
                        renderInput={(params) => (
                          <TextField {...params} placeholder="e.g. pulse.goal_clarity" />
                        )}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        <Box display="flex" gap={2} mt={3}>
          <Button variant="contained" onClick={handleSaveGroups} disabled={isSavingGroups}>
            {isSavingGroups ? 'Saving...' : 'Save Groups'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
